import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineShare } from "react-icons/hi";
import type { ArtworkMedium } from "../lib/artworkPaths";
import { shareArtwork } from "../lib/artworkShare";

type ArtworkShareButtonProps = {
  medium: ArtworkMedium;
  id: string;
  title: string;
  text?: string;
  className?: string;
  children?: React.ReactNode;
};

export function ArtworkShareButton({
  medium,
  id,
  title,
  text,
  className,
  children,
}: ArtworkShareButtonProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleShare = useCallback(async () => {
    try {
      const result = await shareArtwork({ medium, id, title, text });
      if (result === "copied") {
        setCopied(true);
        if (resetTimerRef.current !== null) {
          window.clearTimeout(resetTimerRef.current);
        }
        resetTimerRef.current = window.setTimeout(() => setCopied(false), 2200);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
    }
  }, [medium, id, title, text]);

  return (
    <button
      type="button"
      onClick={handleShare}
      className={className}
      aria-live="polite"
      aria-label={t("artwork.sharePiece")}
    >
      <HiOutlineShare className="h-4 w-4 shrink-0" aria-hidden />
      {copied ? t("artwork.linkCopied") : (children ?? t("artwork.share"))}
    </button>
  );
}
